import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardShell from "@/components/DashboardShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Copy, Check, Clock, Calendar, Tag,
  ExternalLink, Code, History, Building2, User,
  Key, Globe, Sparkles,
  CheckCircle2, FilePenLine, FileText, Link2, Unlink, Plus, Trash2, Pencil
} from "lucide-react";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = BACKEND_URL + "/api";

function CopyField({ label, value, mono = true }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === "—") return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-300">{"—"}</span>
    </div>
  );
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 group">
      <span className="text-sm text-gray-500 font-medium">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className={`text-sm text-gray-900 ${mono ? "font-mono" : ""} max-w-[320px] truncate select-all`} title={value}>{value}</span>
        <button
          onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-100"
          data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
        </button>
      </div>
    </div>
  );
}

function CopyBlock({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
        <button
          onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          {copied ? <><Check className="w-3 h-3 text-green-600" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
      </div>
      <code className="block text-xs bg-gray-50 text-gray-700 px-3 py-2 rounded-lg font-mono select-all break-all border">{value}</code>
    </div>
  );
}

export default function LayoutInfoPage() {
  const { layoutId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/admin/layouts/${layoutId}/info`);
        setData(res.data);
      } catch (err) {
        console.error("Failed to fetch layout info:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [layoutId]);

  if (loading) return (
    <DashboardShell pageTitle="Layout Info" activeItem="layouts">
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-violet-600 border-t-transparent" />
      </div>
    </DashboardShell>
  );

  if (!data) return (
    <DashboardShell pageTitle="Layout Info" activeItem="layouts">
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Layout not found</p>
          <Button variant="outline" onClick={() => navigate("/dashboard?tab=layouts")} data-testid="back-to-layouts">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Layouts
          </Button>
        </div>
      </div>
    </DashboardShell>
  );

  const { layout, event, company, organiser, seat_stats, ticket_types, ticket_type_assignments, history, creation } = data;

  // Friendly host extractor — turn a full URL into the bare domain so the
  // history rows stay readable on mobile ("test-ticket-shop.com" instead of
  // the full long URL with path).
  const hostOf = (url) => {
    if (!url) return null;
    try { return new URL(url).host; } catch { return url; }
  };

  // Pretty print a single history entry's changes object.
  const renderChanges = (changes) => {
    if (!changes || typeof changes !== "object") return null;
    const entries = Object.entries(changes).filter(([, v]) => v !== null && v !== undefined && v !== "");
    if (entries.length === 0) return null;
    return (
      <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5">
        {entries.map(([key, val]) => (
          <span key={key}>
            <span className="text-gray-400">{key}:</span>{" "}
            <span className="font-mono text-gray-700 break-all">
              {typeof val === "object" ? JSON.stringify(val).slice(0, 120) : String(val).slice(0, 120)}
            </span>
          </span>
        ))}
      </div>
    );
  };

  // Map each audit event_type to a friendly label + icon + accent colour so
  // the timeline reads like a story instead of raw enum strings.
  const EVENT_META = {
    layout_created:        { label: "Created",          icon: Plus,         tone: "bg-violet-50 text-violet-700 border-violet-200",   dot: "bg-violet-500" },
    layout_update:         { label: "Updated",          icon: Pencil,       tone: "bg-slate-50 text-slate-700 border-slate-200",      dot: "bg-slate-500" },
    layout_published:      { label: "Published",        icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
    layout_unpublished:    { label: "Saved as Draft",   icon: FileText,     tone: "bg-amber-50 text-amber-700 border-amber-200",      dot: "bg-amber-500" },
    layout_event_linked:   { label: "Event Linked",     icon: Link2,        tone: "bg-blue-50 text-blue-700 border-blue-200",         dot: "bg-blue-500" },
    layout_event_unlinked: { label: "Event Unlinked",   icon: Unlink,       tone: "bg-orange-50 text-orange-700 border-orange-200",   dot: "bg-orange-500" },
    layout_deleted:        { label: "Deleted",          icon: Trash2,       tone: "bg-red-50 text-red-700 border-red-200",            dot: "bg-red-500" },
  };
  const metaFor = (type) =>
    EVENT_META[type] || { label: (type || "Event").replace(/_/g, " "), icon: FilePenLine, tone: "bg-gray-50 text-gray-700 border-gray-200", dot: "bg-gray-400" };

  return (
    <DashboardShell pageTitle="Layout Info" activeItem="layouts">
      {/* Sub-header with back + layout name */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard?tab=layouts")} data-testid="back-to-layouts">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Layouts
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{layout.name}</h2>
            <p className="text-xs text-gray-500 font-mono">{layout.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Publish-status pill — green when live, red when draft. Replaces
              the previous neutral Badge so the state is glanceable. */}
          {layout.is_published ? (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold"
              data-testid="layout-status-badge"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Published
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 text-xs font-semibold"
              data-testid="layout-status-badge"
            >
              <span className="w-2 h-2 rounded-full bg-red-500" />
              Draft (Not Published)
            </span>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate(`/builder/${layout.id}`)}>
            Open Builder
          </Button>
        </div>
      </div>

      <div className="space-y-6 max-w-5xl">
        {/* Creation Source — shows who/what/when/where created this layout.
            For API-created layouts it surfaces the API key name + origin URL
            so ticketing-company admins can audit which integration of theirs
            (production vs staging vs partner site) generated each layout.
            Dashboard-created layouts show the user's email instead. */}
        {creation && (
          <Card className="p-5 border-l-4 border-l-violet-500" data-testid="layout-creation-card">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600" /> Creation
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                <span className="text-sm text-gray-500 font-medium">Source</span>
                <Badge
                  variant="outline"
                  className={
                    creation.via === "api"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 capitalize"
                      : "bg-slate-50 text-slate-700 border-slate-200 capitalize"
                  }
                  data-testid="creation-source-badge"
                >
                  {creation.via === "api" ? "API" : "Dashboard"}
                </Badge>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                <span className="text-sm text-gray-500 font-medium">Created at</span>
                <span className="text-sm text-gray-900">
                  {creation.at ? new Date(creation.at).toLocaleString() : "—"}
                </span>
              </div>

              {creation.via === "api" ? (
                <>
                  <CopyField label="API Key" value={creation.api_key_name || "—"} mono={false} />
                  <CopyField label="Origin Website" value={creation.origin || "—"} mono={false} />
                  {creation.user_agent && (
                    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 sm:col-span-2">
                      <span className="text-sm text-gray-500 font-medium">User Agent</span>
                      <span className="text-xs text-gray-700 font-mono max-w-[320px] truncate" title={creation.user_agent}>
                        {creation.user_agent}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <CopyField label="Created by" value={creation.user_email || creation.user_name || "—"} mono={false} />
                  <CopyField label="User name" value={creation.user_name || "—"} mono={false} />
                </>
              )}
            </div>
          </Card>
        )}

        {/* Top Row: Layout Details + Event Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Layout Details */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4 text-violet-600" /> Layout Details
            </h2>
            <CopyField label="Layout ID" value={layout.id} />
            <CopyField label="Name" value={layout.name} mono={false} />
            <CopyField label="Venue" value={layout.venue_name} mono={false} />
            <CopyField label="Total Seats" value={seat_stats.total?.toString()} />
            <CopyField label="Available" value={seat_stats.available?.toString()} />
            <CopyField label="Booked" value={seat_stats.booked?.toString()} />
            <CopyField label="Held" value={seat_stats.held?.toString()} />
            <CopyField label="Created" value={layout.created_at ? new Date(layout.created_at).toLocaleString() : "—"} mono={false} />
            <CopyField label="Last Updated" value={layout.updated_at ? new Date(layout.updated_at).toLocaleString() : "—"} mono={false} />
          </Card>

          {/* Event Details */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-violet-600" /> Event Details
            </h2>
            <CopyField label="Event ID" value={event.event_id} />
            <CopyField label="Event Name" value={event.event_name} mono={false} />
            <CopyField label="Event Date" value={event.event_date} />
            <CopyField label="Event Time" value={event.event_time} />
            <CopyField label="Location" value={event.event_location} mono={false} />

            <div className="mt-4 pt-3 border-t">
              <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-violet-600" /> Ticketing Company
              </h2>
              <CopyField label="Company ID" value={company?.company_id} />
              <CopyField label="Company Name" value={company?.company_name} mono={false} />
              <CopyField label="Website" value={company?.website} />
              <CopyField label="Contact" value={company?.contact_email} />
            </div>

            {organiser && (
              <div className="mt-4 pt-3 border-t">
                <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-violet-600" /> Organiser
                </h2>
                <CopyField label="Organiser ID" value={organiser.organiser_id} />
                <CopyField label="Name" value={organiser.name} mono={false} />
                <CopyField label="Email" value={organiser.email} />
              </div>
            )}
          </Card>
        </div>

        {/* API Endpoints */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Code className="w-4 h-4 text-violet-600" /> API Endpoints
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { label: "Get Layout", value: `GET /api/external/layouts/${layout.id}` },
              { label: "Update Layout", value: `POST /api/external/layouts/${layout.id}` },
              { label: "Link Event", value: `POST /api/external/layouts/${layout.id}/link-event` },
              { label: "Add Ticket Types", value: `POST /api/external/layouts/${layout.id}/ticket-types` },
              { label: "Assign Tickets", value: `POST /api/external/layouts/${layout.id}/assign-ticket-types` },
              { label: "Availability", value: `GET /api/external/layouts/${layout.id}/availability` },
              { label: "Publish", value: `PATCH /api/external/layouts/${layout.id}/publish` },
              { label: "Delete", value: `DELETE /api/external/layouts/${layout.id}` },
            ].map(({ label, value }) => (
              <CopyBlock key={label} label={label} value={value} />
            ))}
          </div>

          <div className="mt-4 pt-4 border-t">
            <CopyBlock
              label="Embed Code"
              value={`<iframe src="https://seatpilot.co.uk/widget/${layout.id}" width="100%" height="600" frameborder="0"></iframe>`}
            />
          </div>

          <div className="mt-3">
            <CopyBlock label="Widget URL" value={`https://seatpilot.co.uk/widget/${layout.id}`} />
          </div>

          <div className="mt-3">
            <CopyBlock label="Builder URL (Edit)" value={`https://seatpilot.co.uk/builder/${layout.id}`} />
          </div>
        </Card>

        {/* Ticket Types */}
        {ticket_types.length > 0 && (
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Tag className="w-4 h-4 text-violet-600" /> Ticket Types ({ticket_types.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">ID</th>
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">Name</th>
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">Type</th>
                    <th className="text-right py-2 px-3 text-gray-600 font-medium">Price</th>
                    <th className="text-right py-2 px-3 text-gray-600 font-medium">Qty</th>
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">Sales Window</th>
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">Visibility</th>
                  </tr>
                </thead>
                <tbody>
                  {ticket_types.map((tt) => (
                    <tr key={tt.ticket_type_id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3 font-mono text-xs">{tt.ticket_type_id}</td>
                      <td className="py-2 px-3 font-medium">{tt.name}</td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="text-xs">{tt.type}</Badge>
                      </td>
                      <td className="py-2 px-3 text-right font-mono">{tt.currency || "GBP"} {tt.price?.toFixed(2)}</td>
                      <td className="py-2 px-3 text-right">{tt.available_quantity}</td>
                      <td className="py-2 px-3 text-xs text-gray-500">
                        {tt.sales_start?.split("T")[0]} {"—"} {tt.sales_end?.split("T")[0]}
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant={tt.visibility === "public" ? "default" : "secondary"} className="text-xs">
                          {tt.visibility}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {ticket_type_assignments.length > 0 && (
              <div className="mt-4 pt-3 border-t">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Assignments ({ticket_type_assignments.length})</h3>
                <div className="space-y-1">
                  {ticket_type_assignments.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                      <Badge variant="outline" className="text-xs capitalize">{a.assignment_type}</Badge>
                      <span className="font-mono">{a.target}</span>
                      <span className="text-gray-400">&rarr;</span>
                      <span className="font-medium">{ticket_types.find(t => t.ticket_type_id === a.ticket_type_id)?.name || a.ticket_type_id}</span>
                      <span className="text-gray-400 ml-auto">P{a.priority}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Change History — full audit trail of who did what, where, and when.
            Each row shows: action type, the API key name OR dashboard user email,
            origin domain (for API-driven actions), changes diff, and timestamp. */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <History className="w-4 h-4 text-violet-600" /> Change History
            <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1.5 font-normal text-gray-500 border-gray-200">
              {history.length} {history.length === 1 ? "event" : "events"}
            </Badge>
          </h2>
          {history.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No changes recorded yet. Changes via API or Dashboard will appear here.</p>
          ) : (
            <div className="space-y-3" data-testid="layout-history-list">
              {history.map((entry, i) => {
                const isApi = entry.source === "api" || (!!entry.api_key_id && entry.source !== "dashboard");
                const callerLabel =
                  entry.api_key_name || entry.user_email || entry.user_name || entry.caller || "—";
                const originHost = hostOf(entry.origin);
                const meta = metaFor(entry.type);
                const Icon = meta.icon;
                return (
                  <div
                    key={`${entry.timestamp || ""}-${entry.type || ""}-${i}`}
                    className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0"
                    data-testid={`history-row-${i}`}
                  >
                    <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${meta.tone}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${meta.tone}`}>
                          {meta.label}
                        </span>
                        <Badge
                          variant="outline"
                          className={
                            isApi
                              ? "text-[10px] py-0 px-1.5 bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "text-[10px] py-0 px-1.5 bg-slate-50 text-slate-700 border-slate-200"
                          }
                        >
                          {isApi ? "API" : "Dashboard"}
                        </Badge>
                        <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                          {isApi ? <Key className="w-3 h-3" /> : <User className="w-3 h-3" />}
                          <span className="font-medium text-gray-700">{callerLabel}</span>
                        </span>
                        {originHost && (
                          <span className="text-xs text-gray-500 inline-flex items-center gap-1" title={entry.origin}>
                            <Globe className="w-3 h-3" />
                            <span className="font-mono">{originHost}</span>
                          </span>
                        )}
                      </div>
                      {renderChanges(entry.changes)}
                      <p className="text-xs text-gray-400 mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : "—"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </DashboardShell>
  );
}
